import { Button } from '@odekoteam/doppio';
import { CellContext } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Heading } from 'theme-ui';

import IconEdit from '@/assets/images/IconEdit.svg?icon';
import { LoadingBox } from '@/components/Collection/LoadingBox';
import { ModalPreview } from '@/components/Collection/ModalPreview';
import { ModalReference } from '@/components/Collection/ModalReference';
import {
  formatDateRange,
  formatMarkets,
  POSITIONS,
} from '@/components/Collection/utils';
import DataTable, { DataTableColumn } from '@/components/DataTable';
import {
  GetCatalogHomepageRowsQuery,
  GetWarehousesQuery,
  HomepageCollectionComponentType,
  useGetCatalogHomepageRowsQuery,
} from '@/graphql/types/generated';

type CollectionsHomepageEntry =
  GetCatalogHomepageRowsQuery['catalogHomepageRows'][0]['activeNow'][0];

const POSITION_TO_COMPONENT_TYPE: Record<
  string,
  HomepageCollectionComponentType
> = {
  '1A': HomepageCollectionComponentType.CollectionBanner,
  '1B': HomepageCollectionComponentType.CollectionBanner,
  '2': HomepageCollectionComponentType.CollectionCarousel,
  '3': HomepageCollectionComponentType.CollectionHighlightCarousel,
  '4': HomepageCollectionComponentType.CollectionCarousel,
  '5': HomepageCollectionComponentType.CollectionHighlightCarousel,
  '6': HomepageCollectionComponentType.CollectionCarousel,
  '7': HomepageCollectionComponentType.CollectionHighlightCarousel,
};

const EditCell =
  (positionKey: string) =>
  ({ row }: CellContext<CollectionsHomepageEntry, unknown>) => {
    const navigate = useNavigate();
    const componentType = POSITION_TO_COMPONENT_TYPE[positionKey];
    return (
      <Button
        size="small"
        variant="ghost"
        iconLeft={<IconEdit />}
        onPress={() =>
          navigate(
            `/collections/add-to-homepage?collectionId=${row.original.collection.id}&componentType=${componentType}`,
          )
        }
      />
    );
  };

type Warehouse = GetWarehousesQuery['warehouses'][0];

export default function CatalogHomepageTab({
  warehouses,
  totalWarehouses,
}: {
  totalWarehouses: number;
  warehouses: Warehouse[];
}) {
  const [{ data, fetching }] = useGetCatalogHomepageRowsQuery({
    requestPolicy: 'cache-and-network',
    variables: { date: new Date().toISOString().split('T')[0] },
  });

  if (fetching) return <LoadingBox />;

  const positionDataMap = new Map<
    string,
    {
      activeNow: CollectionsHomepageEntry[];
      scheduled: CollectionsHomepageEntry[];
    }
  >();

  POSITIONS.forEach((pos) => {
    positionDataMap.set(pos.key, { activeNow: [], scheduled: [] });
  });

  data?.catalogHomepageRows.forEach(
    (row: GetCatalogHomepageRowsQuery['catalogHomepageRows'][0]) => {
      positionDataMap.set(row.position, {
        activeNow: row.activeNow || [],
        scheduled: row.scheduled || [],
      });
    },
  );

  const getColumns = (
    positionKey: string,
  ): DataTableColumn<CollectionsHomepageEntry>[] => [
    {
      header: 'Collection Name',
      id: 'name',
      accessorFn: (row) => row.collection.name ?? '(unnamed)',
    },
    {
      header: 'Dates',
      id: 'dates',
      accessorFn: (row) => formatDateRange(row.startDate, row.endDate),
    },
    {
      header: 'Markets',
      id: 'markets',
      accessorFn: (row) => formatMarkets(row.warehouses, totalWarehouses),
    },
    {
      id: 'edit',
      header: '',
      meta: { width: 80 },
      cell: EditCell(positionKey),
    },
  ];

  return (
    <Box sx={{ pt: 3, gap: 4, display: 'flex', flexDirection: 'column' }}>
      {POSITIONS.map((position, index) => (
        <Box key={position.key}>
          <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Heading variant="headingSm" sx={{ mb: 0 }}>
              {position.label}: {position.type}
            </Heading>
            {index === 0 && (
              <Flex sx={{ gap: 3 }}>
                <ModalPreview warehouses={warehouses} />
                <ModalReference />
              </Flex>
            )}
          </Flex>
          <Box>
            <DataTable<CollectionsHomepageEntry>
              columns={getColumns(position.key)}
              containerStyles={{ marginTop: 0, paddingBottom: 0 }}
              data={positionDataMap.get(position.key)?.activeNow || []}
            />
            {(positionDataMap.get(position.key)?.scheduled?.length ?? 0) >
              0 && (
              <DataTable<CollectionsHomepageEntry>
                columns={getColumns(position.key)}
                containerStyles={{ marginTop: 0, paddingTop: 0 }}
                data={positionDataMap.get(position.key)?.scheduled || []}
              />
            )}
          </Box>
        </Box>
      ))}
      <ModalPreview warehouses={warehouses} />
    </Box>
  );
}
